/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
import {generatePalette} from '../../../../utilities/colors';
import moment from 'moment';
import SortedSet from 'collections/sorted-set';
import Map from 'collections/map';
import {time} from '../../../../utilities/perf';
import {round} from '../../../../utilities';
import {flatten} from 'lodash';
import {linear} from 'everpolate';
import {crossPanelLinkingOptions, defaultChartOptions, GRID_COLOR, NORMAL_BORDER_WIDTH} from '../constants';

const labelFunc = function (tooltipItem, data) {
  const ds = data.datasets[tooltipItem.datasetIndex];
  const {y} = ds.data[tooltipItem.index];
  return `${ds.label}: ${round(y, 2)} ${ds.yAxisID}`;
};

const defaultTitleProps = {
  display: true,
  position: 'top',
  fontStyle: 'normal',
  padding: 5,
  fontSize: 14,
};

const titleFunc = function (tooltipItems, data) {
  const years = tooltipItems.map(({datasetIndex, index}) => data.datasets[datasetIndex].data[index].x.format('YYYY'));
  return new SortedSet(years).join(', ');
};

const defaultTooltipOptions = (function () {
  let tooltipCounter = 0;
  // TODO: parameterize function by config
  const maxNrTooltips = (window.config && window.config.panel.maxTooltipItems) || 8;
  return {
    enabled: true,
    mode: 'point',
    cornerRadius: 0,
    callbacks: {
      title: titleFunc,
      label: labelFunc,
      afterBody: function () {
        let footer;
        if (tooltipCounter > maxNrTooltips) {
          footer = `and ${tooltipCounter - maxNrTooltips} more...`;
        }
        tooltipCounter = 0;
        return footer;
      },
    },
    filter: function (tooltipItem, data) {
      const ds = data.datasets[tooltipItem.datasetIndex];
      // hide tooltips for special datasets (e.g. min/max)
      if (!ds.key) return false;
      const showItem = tooltipCounter < maxNrTooltips;
      tooltipCounter++;
      return showItem;
    },
  };
})();

export function getDatasetKeyFunction(crossPanelLinking) {
  if (!crossPanelLinking || crossPanelLinking.length === 0) {
    crossPanelLinking = ['scenario', 'model', 'region', 'unit', 'variable'];
  }
  const attrs = flatten(crossPanelLinking.map(v => crossPanelLinkingOptions[v].attributes));
  return data => attrs.map(attr => data[attr]).join('-');
}

function datasetIdGen(raw) {
  const {runId, region, unit, subannual, variable} = raw;
  return `${runId}-${region}-${unit}-${subannual}-${variable}`;
}

/**
 * Generate dataset label from given raw data sample
 * @param raw data sample
 * @param selection filters
 * @returns {string}
 */
function generateDatasetLabel(raw, selection) {
  const {model, scenario, /* runId, */ version, region, /* unit, */ subannual, variable, is_default: isDefault} = raw;
  const scenarioVersion = isDefault ? scenario : `${scenario} (version: ${version})`;
  const parts = [];
  if (!selection) return '';
  if (selection.runs.length > 1) {
    if (selection.models.length === 1) {
      parts.push(scenarioVersion);
    } else if (selection.scenarios.length === 1) {
      parts.push(model);
    } else {
      parts.push(`${model} | ${scenarioVersion}`);
    }
  }
  if (selection.regions.length > 1) {
    parts.push(region);
  }
  if (selection.characteristics.timeseries.length > 1) {
    parts.push(variable);
  }
  if (selection.timeslices && selection.timeslices.length > 1) {
    parts.push(subannual);
  }
  if (parts.length === 0) {
    parts.push(variable);
  }
  return parts.join(' - ');
}

function generateTitle(selection, scenariosMap, modelsMap, timeseriesMap, regionsMap, timeslicesMap) {
  const parts = [];
  if (!selection) return '';
  if (selection.runs) {
    if (selection.runs.length === 1) {
      if (selection.models.length === 1) {
        parts.push(['model', modelsMap[selection.models[0]]]);
      }
      if (selection.scenarios.length === 1) {
        parts.push(['scenario', scenariosMap[selection.scenarios[0]]]);
      }
    }
  }
  if (selection.regions.length === 1) {
    parts.push(['region', regionsMap[selection.regions[0]]]);
  }
  if (selection.characteristics.timeseries.length === 1) {
    parts.push(['variable', timeseriesMap[selection.characteristics.timeseries[0]].variable]);
  }
  if (selection.timeslices && selection.timeslices.length === 1) {
    parts.push(['subannual', timeslicesMap[selection.timeslices[0]]]);
  }
  return parts.map(([key, value]) => `${key}: ${value}`).join(', ');
}

const scatterLabelFunc = function (tooltipItem, data) {
  const {datasetIndex, index} = tooltipItem;
  const ds = data.datasets[datasetIndex];
  const item = ds.data[index];
  const {x, y} = item;
  return `${ds.label}: ${round(y, 10)} ${ds.yAxisID} in ${x.format('YYYY')}`;
};

/**
 * Adds min/max exis to chart
 * @param data
 * @param options
 * @param minMaxAxes
 * @param extrapolate
 * @return {{data: {datasets: Array}, options: *}}
 */
function addMinMaxToChart({data, options}, {minMaxAxes = [], extrapolate = false} = {}) {
  const {datasets} = data;
  const {scales} = options;
  const {yAxes} = scales;
  const updatedDatasets = [];
  const axesToAdd = new Set(minMaxAxes.length > 0 ? minMaxAxes : yAxes.map(a => a.id));
  yAxes.forEach(axis => {
    const yAxisID = axis.id;
    const axisDatasets = datasets.filter(ds => ds.yAxisID === yAxisID);
    axisDatasets.forEach(ds => updatedDatasets.push(ds));
    if (axesToAdd.has(yAxisID)) {
      const axisDatasetsXY = axisDatasets.map(ds => ds.data.map(({x, y}) => ({x: parseInt(x.format('YYYY'), 10), y})));
      const allXValues = new SortedSet(flatten(axisDatasetsXY.map(ds => ds.map(({x}) => x)))).toArray();
      const interpolatedDatasets = axisDatasetsXY.map(ds => {
        const filtered = ds.filter(({y}) => y !== null);
        const X = filtered.map(({x}) => x);
        const Y = filtered.map(({y}) => y);
        const interpolated = linear(allXValues, X, Y);
        if (extrapolate) return interpolated;
        const minIndex = allXValues.findIndex(x => x === X[0]);
        const maxIndex = allXValues.findIndex(x => x === X[X.length - 1]);
        return interpolated.map((y, i) => (i >= minIndex && i <= maxIndex) ? y : null);
      });
      const maxData = allXValues.map((x, i) => ({
        x: moment(x, 'YYYY'),
        y: Math.max(...interpolatedDatasets.map(ds => ds[i]).filter(v => v !== null)),
      }));
      const minData = allXValues.map((x, i) => ({
        x: moment(x, 'YYYY'),
        y: Math.min(...interpolatedDatasets.map(ds => ds[i]).filter(v => v !== null)),
      }));
      const backgroundColor = 'rgba(128,128,128,0.1)';
      updatedDatasets.push({
        yAxisID,
        fill: '+1',
        backgroundColor,
        borderWidth: 0,
        radius: 0,
        lineTension: 0,
        data: maxData,
      });
      updatedDatasets.push({
        yAxisID,
        fill: '-1',
        backgroundColor,
        borderWidth: 0,
        radius: 0,
        lineTension: 0,
        data: minData,
      });
    }
  });
  return {data: {...data, datasets: updatedDatasets}, options};
}

/**
 * Convert raw panel data to ag-grid-compatible data structure
 * @param panel panel data
 * @param options visualization options
 * @returns {*}
 */
export function timeseriesToTableData(panel, options = {}) {
  const {raw, selection} = panel;
  const {runs, crossPanelLinking, timeslicesEnabled} = options;
  const datasetKey = getDatasetKeyFunction(crossPanelLinking);
  const runsMap = new Map((runs || []).map(r => [r.run_id, r]));
  const [minYear, maxYear] = (selection.ranges && selection.ranges.years) || [-Infinity, Infinity];
  const years = [...new Set(raw.map(d => d.year).filter(y => y >= minYear && y <= maxYear))].sort((a, b) => a === b ? 0 : (a > b ? 1 : -1));
  const toCol = field => ({headerName: field, field, cellRendererFramework: 'YearComponent'});
  const grouped = raw.reduce((acc, next) => {
    const {runId, model, scenario, version, region, unit, subannual, variable, year, value} = next;
    const datasetId = datasetIdGen(next);
    const yearIndex = years.indexOf(year);
    if (yearIndex >= 0) {
      if (!acc.hasOwnProperty(datasetId)) {
        const data = [...new Array(years.length)].map(() => '');
        const run = runsMap.get(runId);
        const key = datasetKey({...next, ...run});
        const isDefault = run && run.is_default;
        const scenarioVersion = isDefault ? scenario : `${scenario} (version: ${version})`;
        acc[datasetId] = {
          key,
          runId,
          model,
          scenario,
          version,
          region,
          unit,
          subannual,
          variable,
          data,
          isDefault,
          scenarioVersion,
        };
      }
      acc[datasetId].data[yearIndex] = value;
    }
    return acc;
  }, {});
  const mapperFunc = ({data, ...rest}) => ({...rest, ...data.reduce((a, b, index) => ({...a, [`${years[index]}`]: b}), {})});
  const rowData = Object.values(grouped).map(mapperFunc);
  const columnDefs = [
    {
      headerName: 'Source',
      children: [
        // order: model, scenario (+ version), region, variable, subannual, unit
        {headerName: 'Model', field: 'model'},
        {headerName: 'Scenario', field: 'scenarioVersion'},
        {headerName: 'Region', field: 'region'},
        {headerName: 'Variable', field: 'variable'},
        ...(timeslicesEnabled ? [{headerName: 'Subannual', field: 'subannual'}] : []),
        {headerName: 'Unit', field: 'unit'},
      ],
    },
    {
      headerName: 'Years',
      children: years.map(y => toCol(`${y}`)),
    },
  ];
  return {years, columnDefs, rowData};
}

const pointStyles = [
  'circle',
  'triangle',
  'rect',
  'rectRounded',
  'rectRot',
  'star',
  'cross',
  'crossRot',
  'dash',
  'line',
];

function timeseriesToScatterChartData(panel, options = {}) {
  const {raw, selection} = panel;
  const {copyright, crossPanelLinking, runs, scenariosMap, modelsMap, characteristics, regionsMap, timeslicesMap} = options;
  const datasetKey = getDatasetKeyFunction(crossPanelLinking);
  const runsMap = new Map((runs || []).map(r => [r.run_id, r]));

  const units = [...new Set(raw.map(d => d.unit))];
  const yAxes = units.map((id, index) => ({
    id,
    scaleLabel: {display: true, labelString: `${id}`},
    type: 'linear',
    position: index % 2 === 0 ? 'left' : 'right',
    gridLines: {color: GRID_COLOR},
    // stacked
  }));
  const axisPointStyleMap = new Map(yAxes.map((axis, index) => [axis.id, pointStyles[index % pointStyles.length]]));
  const [minYear, maxYear] = (selection.ranges && selection.ranges.years) || [-Infinity, Infinity];
  const datasetsMap = {};
  const years = new Set();
  raw.forEach(next => {
    const {unit, year, value, runId} = next;
    if (year < minYear || year > maxYear) {
      return;
    }
    years.add(year);
    const run = runsMap.get(runId);
    const datasetId = datasetIdGen(next);
    if (!datasetsMap.hasOwnProperty(datasetId)) {
      const label = generateDatasetLabel({...next, ...run}, selection);
      const data = [];
      const key = datasetKey({...next, ...run});
      datasetsMap[datasetId] = {
        key,
        label,
        data,
        yAxisID: unit,
        stack: unit,
      };
    }
    datasetsMap[datasetId].data.push({y: value, x: moment(year, 'YYYY')});
  });
  const datasets = Object.values(datasetsMap);
  const colors = time(generatePalette, 'generatePalette')({count: datasets.length});
  datasets.forEach((ds, index) => {
    ds.fill = false;
    ds.lineTension = 0;
    ds.borderColor = colors[index].hex();
    ds.backgroundColor = colors[index].desaturate(0.8).brighten(0.7).hex();
    ds.pointStyle = axisPointStyleMap.get(ds.yAxisID);
    ds.borderWidth = NORMAL_BORDER_WIDTH;
    ds.radius = 4;
    ds.showLine = true;
  });
  const xAxes = [{
    type: 'time',
    scaleLabel: {display: true, labelString: 'Years'},
    time: {round: true, unit: 'year'},
    ticks: {source: 'labels', autoSkip: true, autoSkipPadding: 20, maxRotation: 0},
    distribution: 'linear',
    bounds: 'series',
    position: 'bottom',
    gridLines: {color: GRID_COLOR},
  }];
  const tooltips = {
    ...defaultTooltipOptions,
    callbacks: {label: scatterLabelFunc},
  };
  const title = {
    ...defaultTitleProps,
    text: generateTitle(selection, scenariosMap, modelsMap, characteristics.timeseries, regionsMap, timeslicesMap),
  };
  const labels = years.filter(y => parseInt(y, 10) % 10 === 0).map(y => moment(y, 'YYYY'));
  return {
    data: {datasets, labels},
    options: {...defaultChartOptions, scales: {yAxes, xAxes}, tooltips, animation: {duration: 0}, title, copyright},
  };
}

function timeseriesToLineChartData(panel, options = {}) {
  const {raw, selection} = panel;
  const minMaxTimeseries = panel.selection.minMaxTimeseries || [];
  const {copyright, runs, characteristics, crossPanelLinking, scenariosMap, modelsMap, regionsMap, timeslicesMap} = options;
  const datasetKey = getDatasetKeyFunction(crossPanelLinking);

  if (!raw || raw.length === 0) {
    return {data: {labels: [], datasets: []}, options: {...defaultChartOptions}};
  }
  const runsMap = new Map((runs || []).map(r => [r.run_id, r]));
  const stacked = panel.options.stackedCharts && minMaxTimeseries.length === 0;
  const [minYear, maxYear] = (selection.ranges && selection.ranges.years) || [-Infinity, Infinity];
  const years = [...new Set(raw.map(d => d.year))]
    .filter(y => y >= minYear && y <= maxYear)
    .sort((a, b) => a === b ? 0 : (a > b ? 1 : -1));

  // generate yAxes
  const axisMap = {}, variableToAxisMap = {};
  selection.characteristics.timeseries.forEach(id => {
    const ts = characteristics.timeseries[id];
    let axisId;
    if (selection.scales && selection.scales[id]) {
      axisId = `${ts.unit}|${ts.variable}`;
      axisMap[axisId] = {
        id: axisId,
        scaleLabel: {display: true, labelString: `${ts.variable} (${ts.unit})`},
        type: selection.scales[id],
        position: Object.keys(axisMap) % 2 === 0 ? 'left' : 'right',
        stacked,
      };
    } else {
      axisId = ts.unit;
      if (!axisMap[axisId]) {
        axisMap[axisId] = {
          id: axisId,
          scaleLabel: {display: true, labelString: `${ts.unit}`},
          type: 'linear',
          position: Object.keys(axisMap) % 2 === 0 ? 'left' : 'right',
          stacked,
          gridLines: {color: GRID_COLOR},
        };
      }
    }
    variableToAxisMap[ts.variable] = axisId;
  });
  const yAxes = Object.values(axisMap);

  const axisPointStyleMap = new Map(yAxes.map((axis, index) => [axis.id, pointStyles[index % pointStyles.length]]));

  const datasetsMap = new Map();
  raw.forEach(next => {
    const {unit, year, value, variable, runId} = next;
    const datasetId = datasetIdGen(next);
    const yearIndex = years.indexOf(year);
    if (yearIndex < 0) {
      return;
    }
    if (!datasetsMap.has(datasetId)) {
      const run = runsMap.get(runId);
      const label = generateDatasetLabel({...next, ...run}, selection);
      const key = datasetKey({...next, ...run});
      datasetsMap.set(datasetId, {
        key,
        label,
        data: years.map(y => ({x: moment(y, 'YYYY'), y: null})),
        // yAxisID: unit,
        yAxisID: variableToAxisMap[variable],
        stack: unit,
      });
    }
    const x = moment(year, 'YYYY');
    datasetsMap.get(datasetId).data[yearIndex] = {y: value, x};
  });

  const datasets = datasetsMap.toArray();
  const colors = time(generatePalette, 'generatePalette')({count: datasets.length});
  datasets.forEach((ds, index) => {
    ds.lineTension = 0;
    if (ds.key) {
      ds.fill = stacked;
      ds.borderColor = colors[index].hex();
      ds.backgroundColor = colors[index].desaturate(0.8).brighten(0.7).hex();
      ds.pointStyle = axisPointStyleMap.get(ds.yAxisID);
      ds.borderWidth = NORMAL_BORDER_WIDTH;
      ds.radius = 4;
      ds.spanGaps = true;
    }
  });

  const xAxes = [{
    type: 'time',
    scaleLabel: {display: true, labelString: 'Years'},
    time: {round: true, unit: 'year'},
    ticks: {
      min: moment(years[0], 'YYYY'),
      max: moment(years[years.length - 1], 'YYYY'),
      source: 'labels',
      autoSkip: true,
      autoSkipPadding: 20,
      maxRotation: 0,
    },
    distribution: 'linear',
    bounds: 'series',
    position: 'bottom',
    stacked,
    gridLines: {color: GRID_COLOR},
  }];
  const animation = {duration: 0};
  const title = {
    ...defaultTitleProps,
    text: generateTitle(selection, scenariosMap, modelsMap, characteristics.timeseries, regionsMap, timeslicesMap),
  };
  const labels = years.filter(y => parseInt(y, 10) % 10 === 0).map(y => moment(y, 'YYYY'));
  let mappedData = {
    data: {datasets, labels},
    options: {
      ...defaultChartOptions,
      scales: {yAxes, xAxes},
      tooltips: defaultTooltipOptions,
      animation,
      title,
      copyright,
    },
  };
  if (minMaxTimeseries.length > 0) {
    const minMaxVariables = new Set(minMaxTimeseries.map(id => characteristics.timeseries[id]).filter(i => i).map(ts => ts.variable)).toArray();
    const minMaxAxes = minMaxVariables.map(v => variableToAxisMap[v]);
    mappedData = addMinMaxToChart(mappedData, {minMaxAxes, extrapolate: false});
  }
  return mappedData;
}

function minMaxForBarChart(years) {
  const first = years[0];
  const length = years.length;
  if (length === 1) return {min: moment(first, 'YYYY'), max: moment(first, 'YYYY')};
  if (length > 1) {
    const second = years[1];
    const last = years[length - 1];
    const prev = years[length - 2];
    const firstSegment = moment(second, 'YYYY').diff(moment(first, 'YYYY'));
    const lastSegment = moment(last, 'YYYY').diff(moment(prev, 'YYYY'));
    const min = moment(first, 'YYYY').subtract(firstSegment);
    const max = moment(last, 'YYYY').add(lastSegment);
    return {min, max};
  }
  return {};
}

function timeseriesToBarChartData(panel, options = {}) {
  const {raw, selection} = panel;
  const {copyright, crossPanelLinking, runs, characteristics, scenariosMap, modelsMap, regionsMap, timeslicesMap} = options;
  const datasetKey = getDatasetKeyFunction(crossPanelLinking);

  if (!raw || raw.length === 0) {
    return {data: {labels: [], datasets: []}, options: {...defaultChartOptions}};
  }
  const runsMap = new Map((runs || []).map(r => [r.run_id, r]));
  const stacked = panel.options.stackedCharts;
  const [minYear, maxYear] = (selection.ranges && selection.ranges.years) || [-Infinity, Infinity];
  const labels = [...new Set(raw.map(d => d.year).filter(y => y >= minYear && y <= maxYear))]
    .sort((a, b) => a === b ? 0 : (a > b ? 1 : -1));

  const units = [...new Set(raw.map(d => d.unit))];
  const yAxes = units.map((id, index) => ({
    id,
    scaleLabel: {display: true, labelString: `${id}`},
    type: 'linear',
    position: index % 2 === 0 ? 'left' : 'right',
    ticks: {beginAtZero: true},
    stacked,
    gridLines: {color: GRID_COLOR},
  }));

  const datasetsMap = raw.reduce((acc, next) => {
    const {unit, year, value, runId} = next;
    const datasetId = datasetIdGen(next);
    const run = runsMap.get(runId);
    const key = datasetKey({...next, ...run});
    const label = generateDatasetLabel({...next, ...run}, selection);
    const yearIndex = labels.indexOf(year);
    if (yearIndex >= 0) {
      if (!acc.hasOwnProperty(datasetId)) {
        const data = labels.map(y => ({x: moment(y, 'YYYY'), y: null}));
        acc[datasetId] = {key, label, data, yAxisID: unit};
        if (stacked) {
          const {scen_id: scenId, model_id: modelId} = run;
          // acc[datasetId].stack = unit;
          // acc[datasetId].stack = key;
          // https://github.com/iiasa/scenario-explorer-new/issues/76
          acc[datasetId].stack = `${scenId}|${modelId}`;
        }
      }
      acc[datasetId].data[yearIndex] = {y: value, x: moment(year, 'YYYY')};
    }
    return acc;
  }, {});
  const datasets = Object.values(datasetsMap);
  const colors = time(generatePalette, 'generatePalette')({count: datasets.length});
  datasets.forEach((ds, index) => {
    ds.fill = stacked;
    ds.borderColor = colors[index].hex();
    ds.backgroundColor = colors[index].desaturate(0.8).brighten(0.7).hex();
  });

  const title = {...defaultTitleProps, text: generateTitle(selection, scenariosMap, modelsMap, characteristics.timeseries, regionsMap, timeslicesMap)};
  return {
    data: {datasets, labels},
    options: {
      ...defaultChartOptions,
      scales: {
        yAxes,
        xAxes: [{
          type: 'time',
          scaleLabel: {display: true, labelString: 'Years'},
          time: {
            round: true,
            unit: 'year',
          },
          ticks: {
            ...minMaxForBarChart(labels),
            source: 'data',
            // autoSkip: true,
          },
          distribution: 'linear',
          bounds: 'series',
          position: 'bottom',
          stacked,
          gridLines: {color: GRID_COLOR, offsetGridLines: true, offset: true},
        }],
      },
      tooltips: defaultTooltipOptions,
      animation: {duration: 0},
      title,
      copyright,
    },
  };
}

export const emptyMapping = {
  years: [],
  columnDefs: [],
  rowData: [],
  data: {datasets: [], labels: []},
  options: defaultChartOptions,
};

/**
 * Map raw timeseries data to visualization-specific format
 *
 * @param panel panel object
 * @param options additional options such as list of runs, copyright etc
 */
export const mapping = time(function mapping(panel, options = {}) {
  const {showAs} = panel.options;
  const mergedOptions = {...options};
  const {timeseries} = panel.selection.characteristics;
  if (timeseries.length === 0) {
    return emptyMapping;
  } else {
    switch (showAs) {
      case 'chart-scatter':
        return timeseriesToScatterChartData(panel, mergedOptions);
      case 'chart-line':
        return timeseriesToLineChartData(panel, mergedOptions);
      case 'chart-bar':
        return timeseriesToBarChartData(panel, mergedOptions);
      case 'table':
        return timeseriesToTableData(panel, mergedOptions);
      default:
    }
  }
  console.warn(`No data mapping available for panel ${panel.id}`);
  return emptyMapping;
}, 'mapping');
